import { useMemo } from 'react';
import { useAdminFormPage } from '../../hooks/useAdminFormPage';
import { AdminFormPage } from '../../components/AdminFormPage';
import { getWorkflowDefinitionFormConfig } from '../../configs/workflowDefinitionFormConfig';
import { workflowDefinitionsService } from '../../api/workflowDefinitionsService';
import { parseJsonField } from '../../utils/parseJsonField';
import type {
  StoreWorkflowDefinitionRequest,
  UpdateWorkflowDefinitionRequest,
} from '../../api/workflowDefinitionsService';

interface WorkflowDefinitionParams {
  slug?: string;
}

export function WorkflowDefinitionFormPage() {
  const formPage = useAdminFormPage<unknown, WorkflowDefinitionParams>({
    entityName: 'workflow definition',
    getConfig: (mode, params) => getWorkflowDefinitionFormConfig(mode, params.slug),
    getEditId: (params) => params.slug,
    queryKey: (params) => ['admin', 'workflows', params.slug],
    fetchFn: (params) => workflowDefinitionsService.get(params.slug!),
    mapToInitialValues: (entity) => {
      const definition = entity as Record<string, unknown>;
      return {
        slug: definition.slug,
        name: definition.name,
        description: definition.description ?? '',
        is_active: definition.is_active,
        version: definition.version,
        parent_definition_id: definition.parent_definition_id ?? '',
        config: definition.config
          ? JSON.stringify(definition.config, null, 2)
          : '{}',
      };
    },
    mutationFn: async (payload, isEdit, params) => {
      const config = parseJsonField(payload.config);

      if (isEdit && params.slug) {
        const updatePayload: UpdateWorkflowDefinitionRequest = {
          name: payload.name as string,
          description: (payload.description as string) || undefined,
          version: payload.version as number,
          is_active: payload.is_active as boolean,
          config: config ?? undefined,
        };
        return workflowDefinitionsService.update(params.slug, updatePayload);
      }
      const createPayload: StoreWorkflowDefinitionRequest = {
        slug: payload.slug as string,
        name: payload.name as string,
        description: (payload.description as string) || undefined,
        version: payload.version as number,
        is_active: (payload.is_active as boolean) ?? true,
        parent_definition_id: (payload.parent_definition_id as string) || undefined,
        config: config ?? undefined,
      };
      return workflowDefinitionsService.create(createPayload);
    },
    invalidateKey: () => ['admin', 'workflows'],
    navigateBackPath: () => '/admin/workflows',
  });

  const breadcrumbs = useMemo(
    () => [
      { label: 'Admin', href: '/admin' },
      { label: 'Workflows', href: '/admin/workflows' },
      { label: formPage.isEdit ? 'Edit Workflow' : 'Create Workflow' },
    ],
    [formPage.isEdit],
  );

  const errorBreadcrumbs = useMemo(
    () => [
      { label: 'Admin', href: '/admin' },
      { label: 'Workflows', href: '/admin/workflows' },
      { label: 'Edit' },
    ],
    [],
  );

  return (
    <AdminFormPage
      formPage={formPage}
      entityLabel="Workflow Definition"
      breadcrumbs={breadcrumbs}
      errorBreadcrumbs={errorBreadcrumbs}
      genericFetchErrorMessage="An unexpected error occurred while loading the workflow definition."
    />
  );
}
